import { useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { autoSyncHealthConnect } from '../../utils/healthConnect';

/**
 * Pulls Health Connect data in on app open, mirroring FitbitAutoSync.
 *
 * Health Connect is the route every Android-side device takes — Garmin, Samsung,
 * Coros, the phone's own sensors — and until now the only thing that moved that
 * data was a Sync button in Settings. An integration that depends on somebody
 * remembering a button exists is not really connected, and the gap is invisible:
 * everything looks fine, the data simply never arrives.
 *
 * Renders nothing, no-ops entirely off Android, never requests permission (the
 * Settings button owns that conversation), and fails silently. The throttle
 * lives in autoSyncHealthConnect so the "how often" rule has one home.
 */
function HealthConnectAutoSync() {
  const { user, loading } = useAuth();

  useEffect(() => {
    if (loading || !user) return undefined;

    // Also on resume: a phone that sat in a pocket overnight comes back with a
    // night of sleep and HRV waiting, and that is exactly when this should run.
    const run = () => { autoSyncHealthConnect(); };
    run();

    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') run();
    };
    document.addEventListener('visibilitychange', onVisibilityChange);
    return () => document.removeEventListener('visibilitychange', onVisibilityChange);
  }, [user, loading]);

  return null;
}

export default HealthConnectAutoSync;
